import axios, { AxiosInstance, AxiosRequestConfig, type AxiosError } from 'axios';
import { ENV } from '../../config/env';
import { ApiResponse, TokenProvider } from './types';
import { mapAxiosError } from './errorMapper';

let tokenProvider: TokenProvider | null = null;

export const setTokenProvider = (provider: TokenProvider) => { tokenProvider = provider; };

/** Returns the current bearer token from the registered provider, or null if none. */
export const getStoredToken = async (): Promise<string | null> => {
  if (!tokenProvider) { return null; }
  return tokenProvider();
};

const baseURL = ENV.USE_DIRECT_API ? ENV.API_BASE_URL : '';

const instance: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

instance.interceptors.request.use(async config => {
  if (tokenProvider) {
    const token = await tokenProvider();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function proxyFallbackNeeded(error: unknown): boolean {
  const axiosErr = error as AxiosError | undefined;
  const code = axiosErr?.code;
  const status = axiosErr?.response?.status;
  return !ENV.USE_DIRECT_API && (code === 'ECONNREFUSED' || code === 'ERR_NETWORK' || status === 502 || status === 503 || status === 504);
}

async function directRetry<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const abs = new URL(url, ENV.API_BASE_URL).toString();
    const retry = await axios.get<T>(abs, { ...config, baseURL: undefined });
    return { success: true, data: retry.data };
  } catch (err) {
    return mapAxiosError<T>(err, url);
  }
}

function parseRetryAfter(header: unknown): number | null {
  if (!header) return null;
  const v = String(header).trim();
  // if numeric, it's seconds
  const asNum = Number(v);
  if (!Number.isNaN(asNum)) return Math.max(0, Math.floor(asNum * 1000));
  // otherwise try HTTP-date
  const parsed = Date.parse(v);
  if (!Number.isNaN(parsed)) {
    const diff = parsed - Date.now();
    return Math.max(0, diff);
  }
  return null;
}

/**
 * Return a random integer in [0, max) when available.
 * Falls back to Math.random() for older environments.
 */
function secureRandomInt(max: number): number {
  // Use globalThis.crypto when available
  if (typeof globalThis !== 'undefined') {
    const maybeCrypto = (globalThis as unknown as { crypto?: { getRandomValues?: (arr: Uint32Array) => void } }).crypto;
    if (maybeCrypto?.getRandomValues) {
      const arr = new Uint32Array(1);
      maybeCrypto.getRandomValues(arr);
      return arr[0] % max;
    }
  }

  // Fallback
  return Math.floor(Math.random() * max);
}

export async function httpGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const MAX_RETRIES = 3;
  const BASE_DELAY_MS = 500; // base for exponential backoff

  function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await instance.get<T>(url, config);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      // If proxy fallback is needed (local dev proxy not available), try direct once immediately
      if (proxyFallbackNeeded(err)) return directRetry<T>(url, config);

      const axiosErr = err as AxiosError | undefined;
      const status = axiosErr?.response?.status;

      // Handle 429 Too Many Requests for idempotent GET: respect Retry-After and retry with backoff
      if (status === 429 && attempt < MAX_RETRIES) {
          const retryAfterHeader = axiosErr?.response?.headers?.['retry-after'] || axiosErr?.response?.headers?.['Retry-After'];
  const retryMs = parseRetryAfter(retryAfterHeader) ?? (BASE_DELAY_MS * Math.pow(2, attempt) + secureRandomInt(300));
        // wait then retry
        await sleep(retryMs);
        continue; // next attempt
      }

      // Non-429 or max retries reached
      return mapAxiosError<T>(err, url);
    }
  }

  // Fallback - should not reach here
  return { success: false, data: null, error: { message: 'Retry attempts exhausted', url } };
}

export async function httpPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const res = await instance.post<T>(url, data, config);
    return { success: true, data: res.data };
  } catch (err: unknown) {
    // If proxy fallback is needed (local dev proxy not available), try direct once immediately
    if (proxyFallbackNeeded(err)) {
      try {
        const abs = new URL(url, ENV.API_BASE_URL).toString();
        const retry = await axios.post<T>(abs, data, { ...config, baseURL: undefined });
        return { success: true, data: retry.data };
      } catch (retryErr) {
        return mapAxiosError<T>(retryErr, url);
      }
    }
    return mapAxiosError<T>(err, url);
  }
}
export async function httpPut<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const res = await instance.put<T>(url, data, config);
    return { success: true, data: res.data };
  } catch (err: unknown) {
    return mapAxiosError<T>(err, url);
  }
}

export async function httpPatch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const res = await instance.patch<T>(url, data, config);
    return { success: true, data: res.data };
  } catch (err: unknown) {
    return mapAxiosError<T>(err, url);
  }
}

export async function httpDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const res = await instance.delete<T>(url, config);
    return { success: true, data: res.data };
  } catch (err: unknown) {
    return mapAxiosError<T>(err, url);
  }
}
